import { CustomFormApi, Form } from 'informed';
import { useSearchParams } from 'next/navigation';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { scrollToPromoCodeInput } from './helpers';
import { AnalyticsEventName } from '@/api/analytics';
import {
  CouponCodeStatus,
  setCouponCode as requestPromocodeActivation,
} from '@/api/auth';
import { Button } from '@/components/buttons';
import { Input } from '@/components/form/Input';
import { ThemeMode } from '@/helpers/enums';
import { showTooltip } from '@/helpers/tooltip';
import { groupMemberDuplicate, groupMemberNoDeposits } from '@/helpers/user';
import { required } from '@/helpers/validators';
import { Intercom } from '@/hooks/3dParty/intercom';
import { useAnalytics } from '@/hooks/analytics';
import { sendOtherPromoNDBCodeApplyEvent } from '@/modules/googleAnalytics/events/otherEvents';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore, useModalsStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScHavePromocode, ScPromocodeFormWrapper } from './styled';

const PROMOCODE_SECTION_QUERY = 'promocode';
const DISTANCE_TO_TOP = 100;

interface IPromocodeForm {
  promocode: string;
}

export interface IHavePromocodeSectionProps {
  onPromocodeActivationSuccess: () => void;
}

export const HavePromocodeSection: FC<IHavePromocodeSectionProps> = ({
  onPromocodeActivationSuccess,
}) => {
  const formApiRef = useRef<CustomFormApi<IPromocodeForm>>();
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation(['bonuses', 'promotions']);
  const { sendEventToAnalytics } = useAnalytics();
  const searchParams = useSearchParams();
  const user = useCommonStore.use.user();
  const { isMobileDevice } = useServer();

  const { blackColor } = useTheme();

  const [promocodePending, setPromocodePending] = useState(false);

  const [isError, setIsError] = useState(false);
  const [isErrorSupportClicked, setIsErrorSupportClicked] = useState(false);

  const handleChange = useCallback(() => {
    setIsError(false);
    formApiRef.current?.setError('promocode', undefined);
  }, []);

  const setErrorTranslation = useCallback(
    (i18nKey: string) =>
      formApiRef.current?.setError(
        'promocode',
        <Trans
          ns="promotions"
          i18nKey={i18nKey}
          components={{
            s: (
              <strong
                id="intercomcustomLauncher"
                onClick={() => setIsErrorSupportClicked(true)}
              />
            ),
          }}
        />
      ),
    [t]
  );

  const validate = useCallback(() => {
    if (!user) return false;
    let i18nKey;

    if (groupMemberDuplicate(user)) {
      i18nKey = 'validation.duplicate';
    } else if (!groupMemberNoDeposits(user)) {
      i18nKey = 'validation.invalid';
    } else if (!user.confirmedAt) {
      i18nKey = 'validation.emailNotVerified';
    }

    if (i18nKey) {
      setErrorTranslation(i18nKey);
      setIsError(true);
      return false;
    }
    return true;
  }, [user, setErrorTranslation]);

  const handleSubmit = useCallback(
    async ({ promocode }: IPromocodeForm) => {
      if (!validate()) {
        const error = formApiRef.current?.getError('promocode')?.props?.i18nKey;
        sendOtherPromoNDBCodeApplyEvent({
          code: promocode,
          status: 'failed',
          error_data: JSON.stringify(error),
        });
        return;
      }

      setPromocodePending(true);

      try {
        const { data, error } = await requestPromocodeActivation({
          couponCode: promocode,
        });

        if (
          !data ||
          data.status === CouponCodeStatus.FailedToActivate ||
          error
        ) {
          setErrorTranslation('validation.invalid');
          setIsError(true);

          sendEventToAnalytics({
            name: AnalyticsEventName.PROMO__PROMOCODE_ACTIVATION__FAILURE,
            attributes: {
              additionalData: JSON.stringify({ promocode }),
            },
          });

          return;
        }

        if (data.status === CouponCodeStatus.SuccessfullyActivated) {
          showTooltip({
            type: TooltipTypes.SUCCESS,
            body: t('bonuses:claimBonus.successTooltip.description'),
          });

          sendEventToAnalytics({
            name: AnalyticsEventName.PROMO__PROMOCODE_ACTIVATION__SUCCESS,
            attributes: {
              additionalData: JSON.stringify({ promocode }),
            },
          });

          formApiRef.current?.setValue('promocode', '');

          onPromocodeActivationSuccess();

          sendOtherPromoNDBCodeApplyEvent({
            code: promocode,
            status: 'success',
          });

          return;
        }
      } catch (error) {
        sendOtherPromoNDBCodeApplyEvent({
          code: promocode,
          status: 'failed',
          error_data: JSON.stringify(error),
        });
        console.log('Error activating promocode:', error);
      } finally {
        setPromocodePending(false);
      }
    },
    [
      validate,
      t,
      sendEventToAnalytics,
      onPromocodeActivationSuccess,
      setErrorTranslation,
    ]
  );

  useEffect(() => {
    if (!isMobileDevice || !isErrorSupportClicked) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const handler = () => {
      if (!formApiRef.current?.getError('promocode')) return;

      setIsErrorSupportClicked(false);

      timeoutId = setTimeout(() => {
        scrollToPromoCodeInput(ref, DISTANCE_TO_TOP);
      }, 0);
    };

    Intercom.onHide(handler);

    return () => {
      Intercom.offHide(handler);
      clearTimeout(timeoutId);
    };
  }, [isMobileDevice, isErrorSupportClicked]);

  useEffect(() => {
    const block = searchParams.get('block');

    const timeoutId = setTimeout(() => {
      if (block === PROMOCODE_SECTION_QUERY) {
        scrollToPromoCodeInput(ref, DISTANCE_TO_TOP, 'smooth');
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchParams.get('block')]);

  return (
    <ScHavePromocode ref={ref}>
      <div className="header">
        <div className="title">{t('bonuses:claimBonus.title')}</div>
        <div className="description">{t('bonuses:claimBonus.description')}</div>
      </div>
      <Form<IPromocodeForm>
        formApiRef={formApiRef}
        onSubmit={({ values }) => handleSubmit(values)}
      >
        <ScPromocodeFormWrapper>
          <div className="input-wrapper">
            <Input
              autoComplete="off"
              placeholder={t('bonuses:claimBonus.enterCode')}
              name="promocode"
              validateOn="submit"
              validate={required}
              onChange={handleChange}
              mode={ThemeMode.Dark}
              background={blackColor}
              addons={
                <div>
                  <Button
                    isLoading={promocodePending}
                    type="submit"
                    disabled={isError}
                    secondary
                    xSmall
                    autoPaddingHorizontal
                    paddingX={11}
                  >
                    {t('bonuses:activate')}
                  </Button>
                </div>
              }
            />
          </div>
        </ScPromocodeFormWrapper>
      </Form>
    </ScHavePromocode>
  );
};
